import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
    TopologyLink,
    BaselineLink,
    usToMs,
    getLinkStrokeColor,
    getMetricColor,
    LatencyDetail,
    parseCarrier,
    resolveLinkBandwidth,
    formatLoss,
    getLossColor,
    formatMbps,
    isTopologyLinkOfflineOrDropped,
    getOfflineTiming,
    TOPOLOGY_LINK_STROKE,
} from '../TopologyData';
import { getDdmByLink } from '../../Srv6Insight/request/request';
import { algoColor } from '../../Srv6Insight/Srv6Types';

interface LinkDemand {
    dest: string;
    algo: string;
    mbps: number;
    pps: number;
}

interface Props {
    link: TopologyLink;
    baseline: BaselineLink | null;
    rawBaseline?: BaselineLink | null;
    onClose: () => void;
}

const LinkDemands: React.FC<{ source: string; iface: string }> = ({ source, iface }) => {
    const { getAccessTokenSilently } = useAuth0();
    const [demands, setDemands] = useState<LinkDemand[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const token = await getAccessTokenSilently();
                const res = await getDdmByLink(token, { source, interface: iface, timeRange: '5m' });
                if (cancelled) return;
                setDemands(res.data?.data?.demands || []);
            } catch { /* non-critical */ }
            finally { setLoading(false); }
        })();
        return () => { cancelled = true; };
    }, [getAccessTokenSilently, source, iface]);

    if (loading) return <span className="text-[10px] text-semantic-text-secondary italic">Loading demands...</span>;
    if (demands.length === 0) return <span className="text-[10px] text-semantic-text-secondary italic">No SRv6 demands on this interface</span>;

    return (
        <div className="flex flex-col gap-1">
            {demands.map((d, i) => (
                <div key={`${d.dest}-${d.algo}-${i}`} className="flex items-center justify-between text-[10px]">
                    <span className="flex items-center gap-1 text-semantic-text-primary">
                        <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: algoColor(d.algo) }} />
                        {d.dest} <span className="text-semantic-text-secondary">via {d.algo}</span>
                    </span>
                    <span className="font-semibold text-semantic-text-primary">{d.mbps.toFixed(1)} Mbps</span>
                </div>
            ))}
        </div>
    );
};

const LinkDetailPanel: React.FC<Props> = ({
    link,
    baseline,
    rawBaseline = null,
    onClose,
}) => {
    const linkOffline = isTopologyLinkOfflineOrDropped(link, rawBaseline);
    const offlineTiming = linkOffline
        ? getOfflineTiming(rawBaseline?.lastSeenAt)
        : null;
    const color = getLinkStrokeColor(
        link,
        baseline?.avgDelayUs,
        rawBaseline
    );
    const baselineDelta = baseline
        ? link.avgDelayUs - baseline.avgDelayUs
        : null;
    const carrier =
        parseCarrier(link.sourceInterfaceDesc) ??
        parseCarrier(link.targetInterfaceDesc);
    const resolvedBandwidth = resolveLinkBandwidth(link);

    return (
        <div
            className="w-80 h-full overflow-y-auto bg-semantic-bg-primary
                border-l border-semantic-border-primary p-4
                flex flex-col gap-3"
            style={{ fontFamily: '"Quicksand", sans-serif' }}
        >
            {/* Header */}
            <div className="flex items-center justify-between">
                <h3
                    className="text-sm font-bold
                        text-semantic-text-primary"
                >
                    Link Details
                </h3>
                <button
                    onClick={onClose}
                    className="text-semantic-text-secondary
                        hover:text-semantic-text-primary text-lg"
                >
                    &times;
                </button>
            </div>

            {linkOffline && (
                <div
                    className="rounded-md border border-semantic-border-secondary
                        bg-semantic-bg-secondary px-3 py-2 text-xs"
                    style={{ color: TOPOLOGY_LINK_STROKE.offline }}
                >
                    Offline / dropped — no directional telemetry; link was
                    previously tracked in baseline.
                    {offlineTiming && (
                        <div className="mt-1.5 pt-1.5 border-t border-semantic-border-secondary flex flex-col gap-0.5 text-semantic-text-secondary">
                            <span>
                                Last seen up:{' '}
                                <span className="font-semibold text-semantic-text-primary">
                                    {offlineTiming.lastSeenLabel}
                                </span>{' '}
                                ({offlineTiming.lastSeenAgo})
                            </span>
                            <span>
                                Auto-removes:{' '}
                                <span className="font-semibold text-semantic-text-primary">
                                    {offlineTiming.removesInLabel}
                                </span>{' '}
                                if it doesn&apos;t reappear
                            </span>
                        </div>
                    )}
                </div>
            )}

            {/* Endpoints */}
            <Section title="Endpoints">
                <Row label="Source" value={link.source} />
                {link.sourceInterface && (
                    <Row
                        label="Interface"
                        value={link.sourceInterface}
                    />
                )}
                {link.sourceInterfaceDesc && (
                    <Row
                        label="Description"
                        value={link.sourceInterfaceDesc}
                    />
                )}
                <div className="my-1 border-t border-semantic-border-secondary" />
                <Row label="Target" value={link.target} />
                {link.targetInterface && (
                    <Row
                        label="Interface"
                        value={link.targetInterface}
                    />
                )}
                {link.targetInterfaceDesc && (
                    <Row
                        label="Description"
                        value={link.targetInterfaceDesc}
                    />
                )}
            </Section>

            {/* Metrics */}
            <Section title="Metrics">
                <Row
                    label="IS-IS Metric"
                    value={linkOffline ? '—' : String(link.metric)}
                />
                <Row
                    label="Bandwidth"
                    value={resolvedBandwidth.label}
                />
                {carrier && (
                    <Row label="Carrier" value={carrier} />
                )}
                {link.affinity && (
                    <Row label="Affinity" value={link.affinity} />
                )}
                <Row
                    label="Type"
                    value={link.isInterSite ? 'Inter-Site' : 'Intra-Site'}
                />
            </Section>

            {/* Metric vs Latency Correlation — current values are meaningless
                for an offline link, so it's hidden while down. */}
            {!linkOffline && <MetricCorrelation link={link} />}

            {/* Average Latency. For a down link there is no live value; show
                the last-known baseline instead of a misleading "0 µs ▼". */}
            <Section title="Average Latency">
                {linkOffline ? (
                    baseline ? (
                        <div className="flex items-baseline gap-2">
                            <span
                                className="text-lg font-bold"
                                style={{
                                    color: 'var(--color-semantic-text-secondary)',
                                }}
                            >
                                {usToMs(baseline.avgDelayUs)}
                            </span>
                            <span className="text-[10px] uppercase tracking-wide text-semantic-text-secondary">
                                last known
                            </span>
                        </div>
                    ) : (
                        <span className="text-xs italic text-semantic-text-secondary">
                            No data available
                        </span>
                    )
                ) : (
                    <>
                        <div className="flex items-center gap-2">
                            <span
                                className="text-lg font-bold"
                                style={{ color }}
                            >
                                {usToMs(link.avgDelayUs)}
                            </span>
                            {baselineDelta !== null && (
                                <DeltaBadge deltaUs={baselineDelta} />
                            )}
                        </div>
                        {baseline && (
                            <Row
                                label="Baseline"
                                value={usToMs(baseline.avgDelayUs)}
                            />
                        )}
                    </>
                )}
            </Section>

            {/* Forward */}
            <LatencySection
                title="Forward (Source → Target)"
                detail={link.forward}
                baselineAvg={baseline?.forward?.avgDelayUs ?? null}
            />

            {/* Reverse */}
            <LatencySection
                title="Reverse (Target → Source)"
                detail={link.reverse}
                baselineAvg={baseline?.reverse?.avgDelayUs ?? null}
            />

            {/* SRv6 Demands */}
            {link.sourceInterface && (
                <Section title="SRv6 Demands (Forward)">
                    <LinkDemands source={link.source} iface={link.sourceInterface} />
                </Section>
            )}
            {link.targetInterface && (
                <Section title="SRv6 Demands (Reverse)">
                    <LinkDemands source={link.target} iface={link.targetInterface} />
                </Section>
            )}
        </div>
    );
};

const LatencySection: React.FC<{
    title: string;
    detail: LatencyDetail | null;
    baselineAvg: number | null;
}> = ({ title, detail, baselineAvg }) => {
    const hasData = detail?.avgDelayUs != null;

    return (
        <Section title={title}>
            {hasData ? (
                <>
                    <Row
                        label="Avg"
                        value={usToMs(detail.avgDelayUs)}
                    />
                    <Row
                        label="Min"
                        value={usToMs(detail.minDelayUs)}
                    />
                    <Row
                        label="Max"
                        value={usToMs(detail.maxDelayUs)}
                    />
                    <Row
                        label="Variation"
                        value={usToMs(detail.delayVariationUs)}
                    />
                    {detail.lossPercent != null && (
                        <div className="flex justify-between gap-2 text-[11px]">
                            <span className="text-semantic-text-secondary shrink-0">
                                Packet Loss
                            </span>
                            <span
                                className="font-medium text-right"
                                style={{
                                    color: getLossColor(
                                        detail.lossPercent
                                    ),
                                }}
                            >
                                {formatLoss(detail.lossPercent)}
                            </span>
                        </div>
                    )}
                    {(detail.inboundMbps != null ||
                        detail.outboundMbps != null) && (
                        <>
                            <div className="flex justify-between gap-2 text-[11px]">
                                <span className="text-semantic-text-secondary shrink-0">
                                    Rx (5m avg)
                                </span>
                                <span className="text-semantic-text-primary font-medium text-right">
                                    {formatMbps(detail.inboundMbps)}
                                </span>
                            </div>
                            <div className="flex justify-between gap-2 text-[11px]">
                                <span className="text-semantic-text-secondary shrink-0">
                                    Tx (5m avg)
                                </span>
                                <span className="text-semantic-text-primary font-medium text-right">
                                    {formatMbps(detail.outboundMbps)}
                                </span>
                            </div>
                        </>
                    )}
                    {baselineAvg !== null &&
                        detail.avgDelayUs !== null && (
                            <div className="flex items-center gap-1 mt-1">
                                <span
                                    className="text-[10px]
                                        text-semantic-text-secondary"
                                >
                                    vs baseline:
                                </span>
                                <DeltaBadge
                                    deltaUs={
                                        detail.avgDelayUs - baselineAvg
                                    }
                                />
                            </div>
                        )}
                </>
            ) : (
                <span
                    className="text-[11px] text-semantic-text-secondary
                        italic"
                >
                    No data available
                </span>
            )}
        </Section>
    );
};

const MetricCorrelation: React.FC<{ link: TopologyLink }> = ({ link }) => {
    const latencyMs = link.avgDelayUs / 1000;
    const costPerMs = latencyMs > 0
        ? (link.metric / latencyMs).toFixed(1)
        : '-';
    const msPerCost = link.metric > 0
        ? (latencyMs / link.metric).toFixed(2)
        : '-';

    return (
        <Section title="Metric vs Latency">
            <div className="flex items-center gap-2 mb-1">
                <span
                    className="text-sm font-bold"
                    style={{ color: getMetricColor(link.metric) }}
                >
                    Cost {link.metric}
                </span>
                <span className="text-semantic-text-secondary text-[10px]">
                    vs
                </span>
                <span className="text-sm font-bold text-semantic-text-primary">
                    {usToMs(link.avgDelayUs)}
                </span>
            </div>
            <Row label="Cost / ms" value={String(costPerMs)} />
            <Row label="ms / cost unit" value={String(msPerCost)} />
        </Section>
    );
};

const DeltaBadge: React.FC<{ deltaUs: number }> = ({ deltaUs }) => {
    if (Math.abs(deltaUs) < 10) {
        return (
            <span
                className="text-[10px] px-1.5 py-0.5 rounded
                    bg-semantic-bg-success-subtle text-semantic-text-success"
            >
                &#9679; stable
            </span>
        );
    }

    const isWorse = deltaUs > 0;
    return (
        <span
            className={`text-[10px] px-1.5 py-0.5 rounded ${
                isWorse
                    ? 'bg-semantic-bg-danger-subtle text-semantic-text-danger'
                    : 'bg-semantic-bg-success-subtle text-semantic-text-success'
            }`}
        >
            {isWorse ? '▲' : '▼'} {usToMs(Math.abs(deltaUs))}
        </span>
    );
};

const Section: React.FC<{
    title: string;
    children: React.ReactNode;
}> = ({ title, children }) => (
    <div>
        <h4
            className="text-[11px] font-semibold
                text-semantic-text-secondary uppercase tracking-wide
                mb-1"
        >
            {title}
        </h4>
        <div className="flex flex-col gap-0.5">{children}</div>
    </div>
);

const Row: React.FC<{ label: string; value: React.ReactNode }> = ({
    label,
    value,
}) => (
    <div className="flex justify-between gap-2 text-[11px]">
        <span className="text-semantic-text-secondary shrink-0">
            {label}
        </span>
        <span
            className="text-semantic-text-primary font-medium text-right"
            style={{ overflowWrap: 'anywhere' }}
        >
            {value}
        </span>
    </div>
);

export default LinkDetailPanel;
